import type { ControllerParams } from '@wix/yoshi-flow-editor';
import { GroupRole } from '@wix/ambassador-socialgroups-v3-group-member/types';

import * as members from 'store/members';
import { IRootStore } from 'store/index';

import type { IMembersQueryParams } from 'store/members/types';

import {
  MA_ACCOUNT_SETTINGS_APP_ID,
  MA_ACCOUNT_SETTINGS_SECTION_ID,
  MA_ACCOUNT_SETTINGS_WIDGET_ID,
  MEMBERS_APP_DEFINITION_ID,
} from '../../constants';
import type { MembersAreaPublicApi } from '../../types';

export function MembersVM(params: ControllerParams, store: IRootStore) {
  const { siteAPI } = params.flowAPI.viewerScriptFlowAPI;
  let membersAreaPublicApi: Promise<MembersAreaPublicApi> | null = null;

  return {
    members$: {
      fetchProfile,
      fetch,
      query,
      querySuggested,
      fetchJoinRequests,
      fetchAnswers,
      changeRole,
      add,
      inviteByEmail,
      remove,
      approve,
      reject,
      follow,
      unfollow,
      navigateToMember,
      navigateToAccountSettings,
    },
  };

  function getMembersAreaPublicApi() {
    if (!membersAreaPublicApi) {
      membersAreaPublicApi = siteAPI
        .getPublicAPI(MEMBERS_APP_DEFINITION_ID)
        .catch(() => null);
    }
    return membersAreaPublicApi;
  }

  function follow(memberId: string, feedItemId?: string) {
    store.dispatch(members.thunks.follow({ memberId, feedItemId }));
  }

  function unfollow(memberId: string, feedItemId?: string) {
    store.dispatch(members.thunks.unfollow({ memberId, feedItemId }));
  }

  function fetchAnswers(groupId: string, memberId: string) {
    return store.dispatch(members.thunks.fetchAnswers({ groupId, memberId }));
  }

  function changeRole(groupId: string, contactId: string, role: GroupRole) {
    return store.dispatch(
      members.thunks.changeRole({ groupId, contactId, role }),
    );
  }

  function remove(groupId: string, memberId: string) {
    return store.dispatch(members.thunks.remove({ groupId, memberId }));
  }

  function add(groupId: string, memberIds: string[]) {
    return store.dispatch(members.thunks.addToGroup({ groupId, memberIds }));
  }

  function inviteByEmail(groupId: string, emails: string[]) {
    return store.dispatch(members.thunks.inviteByEmail({ groupId, emails }));
  }

  function fetchJoinRequests(groupId: string) {
    return store.dispatch(members.thunks.fetchJoinRequests(groupId));
  }

  function approve(groupId: string, memberId: string) {
    return store.dispatch(members.thunks.approve({ groupId, memberId }));
  }

  function reject(groupId: string, memberId: string, reason?: string) {
    return store.dispatch(members.thunks.reject({ groupId, memberId, reason }));
  }

  function fetchProfile(memberId: string) {
    return store.dispatch(members.thunks.fetchProfile(memberId));
  }
  function querySuggested(
    groupId: string,
    params: IMembersQueryParams,
    offset: number = 0,
    limit?: number,
  ) {
    return store.dispatch(
      members.thunks.querySuggested({
        groupId,
        limit,
        offset,
        query: {
          nickname: params.nickname,
        },
      }),
    );
  }

  function fetch(groupId: string, limit: number = 10, offset: number = 0) {
    return store.dispatch(
      members.thunks.list({
        groupId,
        limit,
        offset,
      }),
    );
  }

  function query(
    groupId: string,
    params: IMembersQueryParams,
    offset: number = 0,
    limit?: number,
  ) {
    return store.dispatch(
      members.thunks.query({
        groupId,
        limit,
        offset,
        query: {
          role: params.role,
          nickname: params.nickname,
        },
      }),
    );
  }

  async function navigateToAccountSettings() {
    const api = await getMembersAreaPublicApi();
    api?.navigateToSection({
      appDefinitionId: MA_ACCOUNT_SETTINGS_APP_ID,
      sectionId: MA_ACCOUNT_SETTINGS_SECTION_ID,
      widgetId: MA_ACCOUNT_SETTINGS_WIDGET_ID,
    });
  }

  async function navigateToMember(params: {
    memberId?: string;
    memberSlug?: string;
  }) {
    const api = await getMembersAreaPublicApi();
    api?.navigateToMember(params);
  }
}

export type IMembersVM = ReturnType<typeof MembersVM>;
